import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { IHelpGuideSummary } from '@apis/help';
import { GuideIconTile } from './GuideIconTile';
import { EnglishOnlyPill } from './EnglishOnlyPill';
import { formatUpdated, groupLabelKey } from './helpFormat';
import styles from './HelpCenterPage.module.css';

interface IGuideCardHeaderProps {
  guide: IHelpGuideSummary;
  badge?: ReactNode;
}

/** Icon, group eyebrow and title shared by the single and parent cards. */
export function GuideCardHeader({ guide, badge }: IGuideCardHeaderProps) {
  const { t } = useTranslation();
  return (
    <div className={styles.cardTop}>
      <GuideIconTile iconKey={guide.iconKey} variant="card" />
      <span>
        <span className={styles.eyebrow}>{t(groupLabelKey(guide.group))}</span>
        <span className={styles.cardTitle}>{guide.title}</span>
      </span>
      <span className={styles.cardTags}>
        <EnglishOnlyPill language={guide.language} />
        {badge}
      </span>
    </div>
  );
}

interface IGuideCardMetaProps {
  first: string;
  second: string;
  updatedAt: string;
  className?: string;
}

/** Two facts plus the "Updated" date, shared by the single and parent cards. */
export function GuideCardMeta({ first, second, updatedAt, className }: IGuideCardMetaProps) {
  const { t, i18n } = useTranslation();
  return (
    <div className={className ? `${styles.meta} ${className}` : styles.meta}>
      <span>{first}</span>
      <span>{second}</span>
      <span className={styles.metaEnd}>{t('help.updated', { date: formatUpdated(updatedAt, i18n.language) })}</span>
    </div>
  );
}
